import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import CryptoJS from "crypto-js";
import logo from "../assets/BizVantage.png";
import FullScreenLoader from "../components/FullScreenLoader";
import { DashboardLayout } from "../components/DashboardLayout";
import {
  TrendingUp,
  FileCheck,
  BookOpen,
  Building2,
  AlertCircle,
  Users,
  Plus,
} from "lucide-react";
import SubscriptionCard from "../components/SubscriptionCard";
import ReferralCard from "../components/ReferralCard";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Link, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { useLanguage } from "../contexts/LanguageContext";

const API_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/assessments";
const SUBSCRIPTION_CHECK_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/user/subscriptionCheck";
const CREATE_SESSION_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/create-subscription-session";

const DashboardNewV1 = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("Core");
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionCheckLoading, setSubscriptionCheckLoading] =
    useState(false);
  const [subscriptionError, setSubscriptionError] = useState("");
  const [newAssessment, setNewAssessment] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    stateProv: "",
    country: "CA",
    vertical: "Vertical 1",
    keyContact: "",
    phone: "",
    email: "",
    position: "Owner",
  });

  const [activeAssessments, setActiveAssessments] = useState([]);
  const [completedAssessments, setCompletedAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const authToken = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.authToken || null;
    } catch {
      return null;
    }
  }, []);

  const registerData = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const userId = useMemo(() => {
    if (!registerData) return null;
    return (
      registerData?.userId ||
      registerData?._id ||
      registerData?.user?._id ||
      null
    );
  }, [registerData]);

  const userEmail = useMemo(
    () =>
      registerData?.email ||
      registerData?.user?.email ||
      registerData?.data?.email ||
      "",
    [registerData],
  );

  const userFullName = useMemo(
    () =>
      registerData?.fullName ||
      registerData?.user?.fullName ||
      registerData?.data?.fullName ||
      "",
    [registerData],
  );

  const userPhone = useMemo(
    () =>
      registerData?.phone ||
      registerData?.user?.phone ||
      registerData?.data?.phone ||
      "",
    [registerData],
  );

  const AES_SECRET = useMemo(
    () =>
      (typeof import.meta !== "undefined" &&
        import.meta.env?.VITE_JWT_SECRET) ||
      process.env.REACT_APP_JWT_SECRET ||
      "",
    [],
  );

  const userName = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      if (!raw) return "User";
      const parsed = JSON.parse(raw);
      return parsed?.fullName || parsed?.user?.fullName || "User";
    } catch {
      return "User";
    }
  }, []);

  const planOption = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      if (!raw) return "CORE";
      const parsed = JSON.parse(raw);
      return parsed?.planOption || parsed?.user?.planOption || "CORE";
    } catch {
      return "CORE";
    }
  }, []);

  const fetchAssessments = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      if (!authToken) {
        setError(t("Not authorized. Please log in."));
        setActiveAssessments([]);
        setCompletedAssessments([]);
        return;
      }

      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken,
        },
        body: JSON.stringify({}),
      });

      const data = await res.json().catch(() => ({}));
      if (data?.success === false) {
        navigate("/");
        return;
      }
      if (!res.ok) {
        throw new Error(data?.message || `Request failed (${res.status})`);
      }

      const { activeList, completedList } = extractLists(data);
      setActiveAssessments(
        activeList.map((a) => ({
          id: a._id || a.id || a.companyName || Math.random().toString(),
          company: a.companyName || a.title || "Untitled",
          progress: a.progress ?? 0,
          assessmentId: a.assessmentId,
          interviewees: Array.isArray(a.interviewees) ? a.interviewees : [],
        })),
      );
      setCompletedAssessments(
        completedList.map((a) => ({
          id: a._id || a.id || a.companyName || Math.random().toString(),
          company: a.companyName || a.title || "Untitled",
        })),
      );
    } catch (e) {
      setError(e.message || t("Failed to fetch assessments."));
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  const handleNewAssessmentClick = async () => {
    setSubscriptionError("");
    if (!authToken) {
      setError("Not authorized. Please log in.");
      return;
    }

    try {
      setSubscriptionCheckLoading(true);
      const body = userId ? { userId } : {};
      const response = await axios.post(SUBSCRIPTION_CHECK_URL, body, {
        headers: {
          "Content-Type": "application/json",
          authorization: authToken,
        },
      });
      const data = response?.data || {};
      if (data?.subscribed) {
        setDialogOpen(true);
        return;
      }
      setSubscriptionDialogOpen(true);
    } catch (err) {
      console.error("subscription check error", err?.response || err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to validate subscription.";
      if (
        err?.response?.status === 401 ||
        String(message).toLowerCase().includes("unauthorized")
      ) {
        navigate("/");
        return;
      }
      setSubscriptionError(message);
      setSubscriptionDialogOpen(true);
    } finally {
      setSubscriptionCheckLoading(false);
    }
  };

  const handleStartCheckoutForPlan = async () => {
    setSubscriptionError("");
    if (!userEmail || !userFullName) {
      setSubscriptionError("Missing account information.");
      return;
    }

    try {
      setSubscriptionLoading(true);
      const encryptedPassword =
        registerData?.password && AES_SECRET
          ? CryptoJS.AES.encrypt(registerData.password, AES_SECRET).toString()
          : undefined;

      const payload = {
        fullName: userFullName.trim(),
        phone: userPhone.trim(),
        planOption: selectedPlan,
        email: userEmail.trim(),
        currency: "USD",
        ...(encryptedPassword ? { password: encryptedPassword } : {}),
      };

      localStorage.setItem("bv.pendingSignup", JSON.stringify(payload));

      const sessionResp = await axios.post(CREATE_SESSION_URL, payload, {
        headers: { "Content-Type": "application/json" },
      });
      const sessionData = sessionResp?.data || {};
      if (sessionData?.url) {
        setSubscriptionDialogOpen(false);
        setSubscriptionLoading(false);
        window.location.href = sessionData.url;
        return;
      }

      setSubscriptionError("Unable to start checkout. Please try again later.");
      localStorage.removeItem("bv.pendingSignup");
    } catch (err) {
      console.error("create session error", err?.response || err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create checkout session.";
      setSubscriptionError(message);
      localStorage.removeItem("bv.pendingSignup");
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const attentionAssessments = activeAssessments.filter((assessment) => {
    const missingFiles = assessment.interviewees.filter((i) => {
      const files = Array.isArray(i.files) ? i.files : [];
      return files.length === 0;
    }).length;
    return missingFiles > 0;
  });

  const stats = [
    {
      label: t("dashboard.activeAssessments"),
      value: String(activeAssessments.length),
      icon: FileCheck,
      change: t("dashboard.currentlyInProgress"),
    },
    {
      label: t("dashboard.completed"),
      value: String(completedAssessments.length),
      icon: BookOpen,
      change: t("dashboard.allTime"),
    },
    {
      label: t("dashboard.total"),
      value: String(activeAssessments.length + completedAssessments.length),
      icon: TrendingUp,
      change: t("dashboard.allAssessments"),
    },
  ];

  const handleCreate = useCallback(async () => {
    if (!authToken) {
      setError("Not authorized. Please log in.");
      return;
    }
    if (!newAssessment.companyName.trim()) {
      setError("Company Name is required.");
      return;
    }

    const sanitizedName = newAssessment.companyName
      .replace(/[^a-zA-Z0-9]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const payload = {
      companyName: sanitizedName?.toUpperCase()?.trim(),
      address1: newAssessment.address1.trim(),
      address2: newAssessment.address2.trim(),
      city: newAssessment.city.trim(),
      state: newAssessment.stateProv,
      country: newAssessment.country,
      vertical: newAssessment.vertical,
      keyContact: newAssessment.keyContact.trim(),
      phone: newAssessment.phone.trim(),
      email: newAssessment.email.trim(),
      position: newAssessment.position,
    };

    try {
      setLoading(true);
      const res = await fetch(API_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success) {
        fetchAssessments();
        setDialogOpen(false);
        setNewAssessment({
          companyName: "",
          address1: "",
          address2: "",
          city: "",
          stateProv: "",
          country: "CA",
          vertical: "Vertical 1",
          keyContact: "",
          phone: "",
          email: "",
          position: "Owner",
        });
        navigate(`/assessments`);
      } else {
        setError(data?.message || "Failed to save assessment.");
      }
    } catch (e) {
      setError(e.message || "Failed to save assessment.");
    } finally {
      setLoading(false);
    }
  }, [authToken, fetchAssessments, navigate, newAssessment]);

  return (
    <DashboardLayout>
      {loading && <FullScreenLoader />}
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Hero section */}
        <div className="rounded-xl bg-sidebar p-8 text-sidebar-foreground flex items-center gap-6">
          <img
            src={logo}
            alt="BizVantage"
            className="h-20 w-auto object-contain brightness-0 invert"
          />
          <p className="text-xl italic text-sidebar-foreground/80 font-display">
            {t("dashboard.aimHigher")}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold font-display tracking-tight text-foreground">
            {t("dashboard.title")} — {userName}
          </h1>
          <Badge className="bg-black text-white border-black text-base font-bold px-2.5 py-0.5">
            {planOption}
          </Badge>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <Card
              key={stat.label}
              className="shadow-[var(--card-shadow)] hover:shadow-[var(--card-shadow-hover)] transition-shadow"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.label}
                </CardTitle>
                <stat.icon className="h-4 w-4 text-accent" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-display">
                  {stat.value}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stat.change}
                </p>
              </CardContent>
            </Card>
          ))}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <div onClick={handleNewAssessmentClick} className="cursor-pointer">
              <Card className="shadow-[var(--card-shadow)] hover:shadow-[var(--card-shadow-hover)] transition-shadow h-full flex flex-col items-center justify-center border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-6 gap-2">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Plus className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-sm font-medium">
                    {subscriptionCheckLoading
                      ? t("dashboard.checking")
                      : t("dashboard.newAssessment")}
                  </span>
                </CardContent>
              </Card>
            </div>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">
                  {t("dashboard.createNewAssessment")}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="companyName">{t("form.companyName")}</Label>
                  <Input
                    id="companyName"
                    value={newAssessment.companyName}
                    onChange={(e) =>
                      setNewAssessment((prev) => ({
                        ...prev,
                        companyName: e.target.value,
                      }))
                    }
                    maxLength={100}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="address1">{t("form.address1")}</Label>
                    <Input
                      id="address1"
                      value={newAssessment.address1}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          address1: e.target.value,
                        }))
                      }
                      maxLength={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address2">{t("form.address2")}</Label>
                    <Input
                      id="address2"
                      value={newAssessment.address2}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          address2: e.target.value,
                        }))
                      }
                      maxLength={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">{t("form.city")}</Label>
                    <Input
                      id="city"
                      value={newAssessment.city}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      maxLength={60}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stateProv">{t("form.stateProv")}</Label>
                    <Input
                      id="stateProv"
                      value={newAssessment.stateProv}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          stateProv: e.target.value,
                        }))
                      }
                      maxLength={60}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">{t("form.country")}</Label>
                    <Input
                      id="country"
                      value={newAssessment.country}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          country: e.target.value,
                        }))
                      }
                      maxLength={60}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vertical">{t("form.vertical")}</Label>
                    <Input
                      id="vertical"
                      value={newAssessment.vertical}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          vertical: e.target.value,
                        }))
                      }
                      maxLength={60}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="keyContact">{t("form.keyContact")}</Label>
                    <Input
                      id="keyContact"
                      value={newAssessment.keyContact}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          keyContact: e.target.value,
                        }))
                      }
                      maxLength={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="position">{t("form.position")}</Label>
                    <Input
                      id="position"
                      value={newAssessment.position}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          position: e.target.value,
                        }))
                      }
                      maxLength={60}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t("form.contactEmail")}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newAssessment.email}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      maxLength={255}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t("form.contactPhone")}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={newAssessment.phone}
                      onChange={(e) =>
                        setNewAssessment((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }))
                      }
                      maxLength={20}
                    />
                  </div>
                </div>
              </div>
              <Button
                onClick={handleCreate}
                disabled={
                  !newAssessment.companyName.trim() ||
                  !newAssessment.email.trim()
                }
                className="w-full"
              >
                {t("Create Assessment")}
              </Button>
            </DialogContent>
          </Dialog>

          <Dialog
            open={subscriptionDialogOpen}
            onOpenChange={setSubscriptionDialogOpen}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display">
                  {t("dashboard.subscriptionRequired")}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm text-muted-foreground">
                <p>{t("dashboard.subscriptionInfo")}</p>
                <p>{t("dashboard.selectPlan")}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { label: "Core", description: "$2,250/qtr" },
                    { label: "Pro", description: "$4,500/qtr" },
                  ].map((plan) => (
                    <button
                      key={plan.label}
                      type="button"
                      onClick={() => setSelectedPlan(plan.label)}
                      className={`rounded-lg border p-4 text-left transition-colors ${
                        selectedPlan === plan.label
                          ? "border-primary bg-primary/5"
                          : "border-border bg-background/80 hover:border-primary"
                      }`}
                    >
                      <p className="font-semibold">{plan.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {plan.description}
                      </p>
                    </button>
                  ))}
                </div>
                {subscriptionError ? (
                  <p className="text-sm text-destructive">
                    {subscriptionError}
                  </p>
                ) : null}
              </div>
              <DialogFooter className="pt-4">
                <Button
                  variant="outline"
                  onClick={() => setSubscriptionDialogOpen(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  onClick={handleStartCheckoutForPlan}
                  disabled={subscriptionLoading}
                >
                  {subscriptionLoading
                    ? t("dashboard.startingCheckout")
                    : `${t("dashboard.select")} ${selectedPlan}`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Active Assessments + Needs Attention */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {/* Active Assessments */}
          <Card className="shadow-[var(--card-shadow)] h-full">
            <CardHeader>
              <CardTitle className="text-lg font-display">
                {t("Active Assessments")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {activeAssessments.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    {t("No assessments found.")}
                  </p>
                ) : (
                  activeAssessments.map((assessment) => (
                    <Link
                      to={`/assessment/${assessment.assessmentId}`}
                      key={assessment.id}
                      className="flex items-center justify-between rounded-lg border p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm">
                          {assessment.company}
                        </p>
                        {(() => {
                          const total = assessment.interviewees.length || 0;
                          const withFiles = assessment.interviewees.filter(
                            (i) => {
                              const files = Array.isArray(i.files)
                                ? i.files
                                : [];
                              return files.length > 0;
                            },
                          ).length;
                          const pct =
                            total === 0
                              ? 0
                              : Math.round((withFiles / total) * 100);
                          return (
                            <>
                              <Progress
                                value={pct}
                                className="h-1.5 mt-2 w-3/4"
                              />
                              <p className="text-[11px] text-muted-foreground mt-1">
                                {pct}% {t("dashboard.complete")}
                              </p>
                            </>
                          );
                        })()}
                      </div>
                      <Badge
                        variant="secondary"
                        className="text-[11px] font-normal shrink-0 ml-3"
                      >
                        {assessment.interviewees.length}{" "}
                        {t("dashboard.interviewee")}
                        {assessment.interviewees.length !== 1 ? "s" : ""}
                      </Badge>
                    </Link>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Needs Attention */}
          <Card className="shadow-[var(--card-shadow)] h-full">
            <CardHeader>
              <CardTitle className="text-lg font-display text-red-400">
                {t("Needs Attention")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {attentionAssessments.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    {t("Nothing needs attention right now.")}
                  </p>
                ) : (
                  attentionAssessments.map((assessment) => {
                    const missingFiles = assessment.interviewees.filter((i) => {
                      const files = Array.isArray(i.files) ? i.files : [];
                      return files.length === 0;
                    }).length;

                    return (
                      <Link
                        to={`/assessment/${assessment.assessmentId}`}
                        key={assessment.id}
                        className="block rounded-lg border p-4 items-center transition-colors cursor-pointer hover:bg-muted/50"
                      >
                        <div>
                          <p className="font-medium text-sm">
                            {assessment.company}
                          </p>
                          <div className="mt-1 space-y-0.5">
                            {missingFiles > 0 && (
                              <p className="text-[11px] text-amber-500">
                                {missingFiles} {t("dashboard.missingFiles")}
                              </p>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Placeholder sections */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SubscriptionCard />
          <ReferralCard />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardNewV1;

function extractLists(data) {
  if (data && Array.isArray(data.active) && Array.isArray(data.completed)) {
    return { activeList: data.active, completedList: data.completed };
  }

  if (Array.isArray(data)) {
    const activeList = data.filter((a) => {
      const s = (a?.status ?? a?.state ?? "").toString().toLowerCase();
      return s === "active" || a?.isActive === true || a?.isCompleted === false;
    });

    const completedList = data.filter((a) => {
      const s = (a?.status ?? a?.state ?? "").toString().toLowerCase();
      return (
        s === "completed" || a?.isCompleted === true || a?.completed === true
      );
    });

    return { activeList, completedList };
  }

  return { activeList: [], completedList: [] };
}
